import { round } from "mathjs";
import { sorting_options } from "@liveflight/utilities/sorting";
import { get_markup } from "@liveflight/utilities/price";
import {
  flight_offer_total_duration,
  flight_offer_average_duration,
} from "@liveflight/utilities/flight-duration";

export function parse_flight_offer_for_ecommerce_item(flightOffer) {
  const { segments: first_itin_segments = [] } =
    flightOffer.itineraries[0] || {};
  const firstSegment = first_itin_segments[0] || {};
  const {
    carrier: first_carrier = {},
    departure: first_departure = {},
    aircraft: first_aircraft = {},
  } = firstSegment;
  const brand = `${flightOffer.source}-${first_carrier.code || ""}`;

  const itemName = flightOffer.itineraries
    .map((itinerary) => {
      return itinerary.segments
        .map((segment) => {
          return `${segment.departure.iataCode}${segment.arrival.iataCode}`;
        })
        .join("-");
    })
    .join("/");

  const price = flightOffer.price.grandTotal;

  const itemId = [
    first_departure.at || "",
    first_aircraft.code || "",
    firstSegment.flightNumber || "",
  ].join("|");

  return {
    item_id: itemId,
    item_name: itemName,
    item_brand: brand,
    price: price,
    item_category: "",
  };
}

export function parse_flight_offers(flightOffers) {
  const flights = flightOffers.data ? flightOffers.data : [];
  const dictionaries = flightOffers.dictionaries
    ? flightOffers.dictionaries
    : {};
  const meta = flightOffers.meta ? flightOffers.meta : {};
  let best_picks = {};
  if (flightOffers.bestFlight && flightOffers.bestFlight.benchmarks) {
    Object.keys(sorting_options).forEach((sorting) => {
      let flight_offer_id = flightOffers.bestFlight.bestOfferId;
      if (sorting === "price") {
        flight_offer_id = flightOffers.bestFlight.benchmarks.bestPriceId;
      }
      if (sorting === "duration") {
        flight_offer_id = flightOffers.bestFlight.benchmarks.bestDurationId;
      }

      const flight_offer = flights.find(
        (f) => f.id === String(flight_offer_id),
      );
      if (flight_offer) {
        best_picks[sorting] = {
          total_duration: flight_offer_total_duration(flight_offer),
          duration: flight_offer_average_duration(flight_offer),
          flight_offer_id: String(flight_offer_id),
          rankings: flightOffers.bestFlight.metadata || {},
          price: {
            ...flight_offer.price,
            ...get_markup(flight_offer),
          },
        };
      }
    });
  }
  return {
    best_picks,
    flights: flights,
    //offers: offersSorted,
    dictionaries: dictionaries,
    meta: meta,
  };
}

export function parse_flight_offers_classes(flight_offer) {
  let classes_by_segment = {
    //segment_id: [classe_pax_1, classe_pax_2, ...]
  };

  let cabins = {
    ECONOMY: "Economy",
    PREMIUM_ECONOMY: "Premium economy",
    BUSINESS: "Business",
    FIRST: "First",
  };

  // in caso di schedule changes possono mancare i fareDetailsBySegment dei segmenti modificati
  // quindi preparo classes_by_segment popolando con gli id dei segmenti che trovo negli itinerari
  // così se manca un fareDetailsBySegment non si pianta il rendering
  flight_offer.itineraries.forEach((it) => {
    it.segments.forEach((seg) => {
      classes_by_segment[seg.id] = [];
    });
  });

  flight_offer.travelerPricings.forEach((t) => {
    if (t.fareDetailsBySegment) {
      t.fareDetailsBySegment.forEach((f) => {
        if (!classes_by_segment[f.segmentId])
          classes_by_segment[f.segmentId] = [];
        if (f.cabin) {
          // "traduce" e pusha
          classes_by_segment[f.segmentId].push(
            cabins[f.cabin] ? cabins[f.cabin] : f.cabin,
          );

          // elimina duplicati
          classes_by_segment[f.segmentId] = classes_by_segment[
            f.segmentId
          ].filter((element, index) => {
            return classes_by_segment[f.segmentId].indexOf(element) === index;
          });
        }
      });
    }
  });

  return classes_by_segment;
}

export function formatPassengerName(passenger, fullName = false) {
  if (!passenger) return "";
  return [
    passenger.firstname,
    fullName ? passenger.middlename : undefined,
    passenger.lastname,
    fullName ? passenger.second_lastname : undefined,
  ]
    .filter((e) => !!e)
    .join(" ");
}

const fullAirportLabel = (endpoint) => {
  const { airportName, iataCode } = endpoint || {};
  if (!airportName) {
    return iataCode;
  }
  return `(${iataCode}) ${airportName}`;
}

export function formatSegmentLabel(segmentInfo, fullDescription = false) {
  const { arrival, departure } = segmentInfo || {};
  if (!departure || !arrival) {
    return "";
  }
  if (!fullDescription) {
    return `${departure.iataCode} › ${arrival.iataCode}`;
  }
  return `${fullAirportLabel(departure)} › ${fullAirportLabel(arrival)}`;
}

/**
 * Look up a value in a segment-keyed map, reconciling the carrier IATA
 * prefix that can differ between an itinerary segment id and the id the
 * carrier stamps on ancillaries / fares. Passes: the id as-is, then with the
 * IATA prefixed, then with a leading IATA stripped. Returns `fallback` when
 * none match.
 *
 * @param {object} map segment-keyed lookup
 * @param {string} segment_id segment id as held by the caller
 * @param {string} carrier_iata airline IATA code reconciled on the retries
 * @param {*} [fallback=undefined] value returned when nothing matches
 * @returns {*} the matched value, or `fallback`
 */
export const get_value_by_segment = (
  map,
  segment_id,
  carrier_iata,
  fallback = undefined
) => {
  const safe_map = map || {};
  if (Object.prototype.hasOwnProperty.call(safe_map, segment_id)) {
    return safe_map[segment_id];
  }
  if (carrier_iata && typeof segment_id === "string") {
    const prefixed = `${carrier_iata}${segment_id}`;
    if (Object.prototype.hasOwnProperty.call(safe_map, prefixed)) {
      return safe_map[prefixed];
    }
    if (segment_id.indexOf(carrier_iata) === 0) {
      const stripped = segment_id.slice(carrier_iata.length);
      if (Object.prototype.hasOwnProperty.call(safe_map, stripped)) {
        return safe_map[stripped];
      }
    }
  }
  return fallback;
};

/**
 * Realign segment ids onto the canonical ids in `id_map` via
 * get_value_by_segment. Unresolvable ids are dropped — a wrong segment is
 * worse than none — unless `keep_unmatched`, which committed ancillaries
 * need or the booked extra vanishes from the resume.
 *
 * @param {Array<string>} ids segment ids as held by the caller
 * @param {object} id_map canonical lookup, keyed and valued by segment id
 * @param {string} carrier_iata airline IATA code reconciled on the retries
 * @param {boolean} [keep_unmatched=false] keep ids that resolve to nothing
 * @returns {Array<string>} realigned segment ids
 */
export const realign_segment_ids = (
  ids,
  id_map,
  carrier_iata,
  keep_unmatched = false
) => {
  const realigned = (Array.isArray(ids) ? ids : []).map((segment_id) =>
    get_value_by_segment(
      id_map,
      segment_id,
      carrier_iata,
      keep_unmatched ? segment_id : null
    )
  );
  return keep_unmatched ? realigned : realigned.filter(Boolean);
};

/**
 * The offer's marketing carrier IATA code, read off the first segment.
 * Used to reconcile carrier-prefixed segment ids in get_value_by_segment.
 *
 * @param {object} flight_offer normalized FlightOffer
 * @returns {string} IATA code, or "" when unavailable
 */
export const offer_carrier_iata = (flight_offer) => {
  const { itineraries = [] } = flight_offer || {};
  const { segments = [] } = itineraries[0] || {};
  const { carrier = {} } = segments[0] || {};
  return carrier.code || "";
};

export const formatPrice = (price) => {
  const roundedPrice = String(round(price, 2));

  if (!roundedPrice.includes(".")) {
    return roundedPrice + ".00";
  }

  try {
    const decimals = roundedPrice.split(".")[1];
    return roundedPrice + (decimals.length === 1 ? "0" : "");
  } catch (e) {
    return roundedPrice;
  }
};

export const capitalize = (text = "") =>
  String(text).charAt(0).toUpperCase() + String(text).slice(1).toLowerCase();

const MONTHS = {
  0: "gennaio",
  1: "febbraio",
  2: "marzo",
  3: "aprile",
  4: "maggio",
  5: "giugno",
  6: "luglio",
  7: "agosto",
  8: "settembre",
  9: "ottobre",
  10: "novembre",
  11: "dicembre",
};

export const verbose_month = (month_index) => MONTHS[month_index] || "";

// replace(/ /g) instead of replaceAll — the jest runtime (.nvmrc → Node 14)
// predates String.replaceAll and core-js 2 has no polyfill for it.
export const as_compare_string = (v) =>
  String(v).toLowerCase().replace(/ /g, '');